<template>
	<div 
	class="scenario absolute inset-0 bg-black" 
	v-if="!hasPendingRequest && currentNode"
	@keyup="playPause">
		<!-- Scenario background scene -->
		<Scene
		v-if="showScene"
		class="faa-fade faa-fade-in"
		ref="scene"
		:scene="currentScene"
		:has-been-played="hasSceneEnded"
		@playing="setScenePlaying"
		@loading="isLoading = true"
		@loaded="isLoading = false"
		@ended="sceneEnded" />

		<!-- Interactive node on top of the scene background -->
		<transition name="faa-fade">
			<Interactive
			v-if="showInteractive"
			:node="currentNode"
			:assets="assets"
			@trigger="triggerClick($event)" />
		</transition>

		<!-- Load for scenes -->
		<transition name="faa-fade">
			<div class="faa-loader" v-if="isLoading">
				<span></span>
				<span></span>
				<span></span>
				<span></span>
			</div>
		</transition>

		<!-- Scenario modal for games, pdf and media -->
		<Modal
		v-if="showModal"
		:node="currentNode"
		:scenario-end="scenarioEnd"
		:assets="assets"
		:games="games"
		:proposals="proposals"
		:game-results="gameResults"
		@game-ended="gameEnded"
		@close="nextNode($event)" />

		<!-- Scenario Main Scene button -->
		<div
		class="scenario-menu-button absolute flex items-center faa-fade"
		:class="{ 'faa-fade-in': enableMenuButton }">
			<div
			class="scenario-menu-button-container bg-blue rounded-full p-3 pt-2"
			:class="{ 'cursor-pointer': enableMenuButton }"
			@click="goToMenu">
				<img src="@/assets/images/home-icon.svg">
			</div>
			<p class="text-white bg-blue-dark rounded px-4 py-2 ml-4">
				Début de l'épisode
			</p>
		</div>

		<!-- Scenario and node custom navigation -->
		<div 
			class="scenario-nav absolute inset-x-0 flex flex-col items-center px-2"
		>
			<!-- Custom node buttons/triggers -->
			<transition name="faa-fade">
				<div
				class="scenario-buttons flex flex-row flex-grow flex-wrap justify-center"
				v-if="showSceneTriggers">
					<button 
					class="faa-button-blue"
					:class="{
						wide: (currentScene.triggers_by_type.scene_button.length > 5),
						'triggered': visitedNodes[button.triggered_node_id]
					}"
					v-for="button in currentScene.triggers_by_type.scene_button"
					:key="button.id"
					:title="button.name" 
					@click.prevent="triggerClick(button)">
						<div class="content">
							<img :src="getButtonIcon(button)">
							<span>{{button.name}}</span>
						</div>
						<div class="progress-container">
							<div class="progress"></div>
						</div>
					</button>
				</div>
			</transition>

			<!-- Scenario controls Play/Pause Previous/Next node -->
			<div
			class="scenario-controls flex flex-row items-center h-12">
				<!-- Previous node button -->
				<div
				class="scenario-previous cursor-pointer mr-4"
				:class="{ disabled: !hasPreviousNode || isLoading }"
				@click.prevent="previousNode">
					<img src="@/assets/images/previous-icon.svg">
				</div>

				<!-- Play/Pause buttons -->
				<div
				class="scenario-play-pause cursor-pointer"
				:class="{ disabled: !enablePlayPause || isLoading }"
				@click.prevent="playPause">
					<img v-if="scenePlaying" src="@/assets/images/pause-icon.svg">
					<img v-else src="@/assets/images/play-icon.svg">
				</div>

				<!-- Next node button -->
				<div
				class="scenario-next cursor-pointer ml-4"
				:class="{ disabled: !hasNextNode || isLoading }"
				@click.prevent="nextNode('next_click')">
					<img src="@/assets/images/next-icon.svg">
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { mapState } from 'vuex'

	import soundEffectMixin from '@/mixins/soundEffectMixin'

	import Scene from '@/components/scenario/Scene'
	import Interactive from '@/components/scenario/Interactive'
	import Modal from '@/components/scenario/Modal'

	export default {
		name: 'Scenario',
		mixins: [soundEffectMixin],
		props: {
			id: {
				type: String,
				required: true,
				default: null,
			},
			nodeId: {
				type: String,
				required: false,
				default: null,
			},
			gamePreview: {
				type: Boolean,
				required: false,
				default: false,
			},
		},
		components: {
			Scene,
			Interactive,
			Modal,
		},
		data() {
			return {
				// UI states
				ready: false,
				pendingRequest: 0,
				isLoading: false,
				scenePlaying: false,
				hasSceneEnded: false,
				scenarioEnd: false,
				breadcrumbs: [
					{
						parent: 'Home',
						label: 'Chargement...',
					}
				],
				// Data states
				nodesLogId: null,
				scenario: null,
				nodeById: null,
				firstNodeId: null,
				navigationSceneStack: [],
				navigationStack: [],
				currentNode: null,
				gameResults: {},
				// Progress tracking
				visitedNodes: {},
				mediaEnded: {},
				nodesStats: {}, // Total nodes count per type
				// Sounds
				soundEffects: {
					trigger: require('../assets/sounds/scene_button.mp3'),
					pdf: require('../assets/sounds/pdf_node.mp3'),
					menu: require('../assets/sounds/scenario_menu_button.mp3'),
					end: require('../assets/sounds/scenario_end.mp3'),
				},
			}
		},
		computed:{
			hasPendingRequest() {
				return (this.pendingRequest > 0)
			},
			...mapState('Auth', [ 'userInfo' ]),
			...mapState('Assets', {
				assets(state) {
					if (!state.list || state.list.length <= 0)
						return {}

					return state.list.reduce((dict, asset) => {
						dict[asset.id] = asset

						return dict
					}, {})
				}
			}),
			...mapState('Games', {
				games(state) {
					if (!state.list || state.list.length <= 0)
						return {}

					return state.list.reduce((dict, game) => {
						dict[game.id] = game

						return dict
					}, {})
				}
			}),
			...mapState('Proposals', {
				proposals(state) {
					if (!state.list || state.list.length <= 0)
						return {}

					return state.list.reduce((dict, proposal) => {
						dict[proposal.id] = proposal

						return dict
					}, {})
				}
			}),
			nodeBreadcrumbs() {
				if (!this.firstNodeId || !this.nodeById)
					return []

				if (!this.nodeById[this.firstNodeId] || !this.nodeById[this.firstNodeId].triggers)
					return []

				let nodeBreadcrumbs = []

				// Create nodes navigation breadcrumbs
				const node = this.nodeById[this.firstNodeId]

				for (var k = 0; k < node.triggers.length; k++) {
					const trigger = node.triggers[k]

					if (trigger.triggerType.slug == 'scene_button') {
						nodeBreadcrumbs.push({
							label: trigger.name,
							nodeId: trigger.triggered_node_id,
						})
					}
				}

				return nodeBreadcrumbs
			},
			nodeProgress() {
				if (!this.nodeById || !this.visitedNodes)
					return {}

				// Count visited nodes by type
				const ids = Object.keys(this.visitedNodes)

				return ids.reduce((dict, nodeId) => {
					const node = this.nodeById[nodeId]

					if (node) {
						if (!dict[node.type.slug]) {
							dict[node.type.slug] = 1
						} else {
							dict[node.type.slug] += 1
						}
					}

					return dict
				}, {})
			},
			currentScene() {
				if (!this.nodeById || !this.navigationSceneStack || this.navigationSceneStack.length <= 0)
					return null

				const id = this.navigationSceneStack[this.navigationSceneStack.length - 1]

				return this.nodeById[id]
			},
			showScene() {
				return (this.currentScene ? true : false)
			},
			showInteractive() {
				return (!this.scenarioEnd && this.currentNode && this.currentNode.type.slug == 'interactive')
			},
			showModal() {
				const modalNodeTypes = ['game', 'pdf', 'media', 'simple_proposal']

				return (this.scenarioEnd || (this.currentNode && modalNodeTypes.indexOf(this.currentNode.type.slug) > -1))
			},
			enableMenuButton() {
				if (!this.navigationStack || !this.navigationSceneStack || this.navigationStack <= 0 || this.navigationSceneStack.length <= 0)
					return false

				// Never show menu button on the first scene (ie. the menu)
				if (this.navigationStack[this.navigationStack.length - 1] == this.navigationSceneStack[0])
					return false

				// Catch some case where we are showing an interactive node on top of the menu
				if (this.navigationSceneStack.length == 1 && this.currentNode.type.slug == 'interactive')
					return false

				// Don't show menu button if the scenario has ended or we are playing an introduction
				return (!this.scenarioEnd && (this.scenario && this.scenario.type.slug != 'intro'))
			},
			showSceneTriggers() {
				if (!this.currentScene || !this.currentNode)
					return false

				// Don't show scene triggers if they don't exist
				if (!this.currentScene.triggers_by_type || !this.currentScene.triggers_by_type.scene_button || this.currentScene.triggers_by_type.scene_button.length <= 0)
					return false

				// Check if we are showing a scene or interactive node
				const validNodeTypes = ['scene', 'interactive']
				
				if (validNodeTypes.indexOf(this.currentNode.type.slug) > -1) {
					// Always show them on static scene
					if (this.isAssetStatic(this.currentScene.node_content))
						return true

					// Otherwise, only show them if the scene has ended and is not playing
					return (this.hasSceneEnded && !this.scenePlaying)
				}

				return false
			},
			enablePlayPause() {
				if (!this.currentNode || !this.currentScene)
					return false

				// Enable on scene or interactive node when the scene is not static
				const validNodeTypes = ['scene', 'interactive']

				if (validNodeTypes.indexOf(this.currentNode.type.slug) > -1) {
					return !this.isAssetStatic(this.currentScene.node_content)
				}

				return false
			},
			hasPreviousNode() {
				if (this.scenarioEnd)
					return false

				// Avoid returning true if we are displaying the first interactive node of the scenario after a scene
				if (this.currentNode && this.currentNode.type.slug == 'interactive' && this.navigationStack.length == 2) {
					return false
				}

				return this.navigationStack.length > 1
			},
			hasNextNode() {
				if (!this.currentNode || !this.currentScene || this.scenarioEnd)
					return false

				// Specific logic for scene and interactive node
				const validNodeTypes = ['scene', 'interactive']

				if (validNodeTypes.indexOf(this.currentNode.type.slug) > -1) {
					const hasButtons = (this.currentScene.triggers_by_type && this.currentScene.triggers_by_type.scene_button &&  this.currentScene.triggers_by_type.scene_button.length > 0)

					// If the node as a default next node, or doesn't have buttons
					if ((this.currentNode.triggers_by_type && this.currentNode.triggers_by_type.continue && this.currentNode.triggers_by_type.continue[0]) || !hasButtons) {
						return true
					}

					// True if the scene has buttons to be shown (and not already showing them)
					return (!this.showSceneTriggers && hasButtons)
				}

				return true
			},
		},
		watch: {
			id: {
				async handler(val) {
					// Load current scenario data, if needed
					if (val) {
						// todo: handle invalid uuid response
						await this.dispatchRequest('Scenarios/getByID', val)
						
						this.scenario = this.$store.state.Scenarios.items[this.id]
					}
				},
				immediate: true
			},
			scenario: {
				async handler(scenario, oldScenario) {
					// Only parse scenario data if everything as been loaded, and we did not already loaded this scenario
					if (scenario && this.ready && (!oldScenario || oldScenario.id != scenario.id)) {
						await this.parseScenarioData()
					}
				},
				immediate: true,
			},
			nodeId(id) {
				if (!id || !this.currentNode || !this.nodeById[id])
					return

				this.navigateToNode(id, true)

				// Reset route to avoid NavigationDuplicated error
				this.$router.push({
					name: 'Scenario',
					params: {
						id: this.id,
					}
				})
			},
		},
		async created() {
			if (this.$store.state.Scenarios.list.length <= 0) {//todo check in store ?
				await this.dispatchRequest('Scenarios/getList')
			}
			if (this.$store.state.Assets.list.length <= 0) {
				await this.dispatchRequest('Assets/getList')
			}
			if (this.$store.state.Games.list.length <= 0) {
				await this.dispatchRequest('Games/getList')
			}
			if (this.$store.state.Proposals.list.length <= 0) {
				await this.dispatchRequest('Proposals/getList')
			}

			// Add keyboard shortcut event for play/pause button
			window.addEventListener('keyup', this.playPause)

			// Load data on first creation
			if (!this.nodeById && this.scenario) {
				await this.parseScenarioData()
			}

			// Update ready state
			this.ready = true
		},
		beforeDestroy() {
			// Remove keyboard shortcut event for play/pause button
			window.removeEventListener('keyup', this.playPause)
		},
		methods: {
			async dispatchRequest(action_name, args = null) {
				this.pendingRequest += 1

				let response = null

				if (args) {
					response = await this.$store.dispatch(action_name, args)
				} else {
					response = await this.$store.dispatch(action_name)
				}

				this.pendingRequest -= 1
				
				return response
			},
			getNodeById() {
				// Handle game debug preview
				if (this.gamePreview) {
					return {
						'0': {
							"id": "0",
							"node_content": this.id,
							"scenario_id": "0",
							"triggers": [],
							"type": {
								"slug": "game",
							},
							"triggers_by_type": {}
						},
					}
				}

				if (!this.scenario || !this.scenario.nodes || this.scenario.nodes.length <= 0)
					return null

				return this.scenario.nodes.reduce((dict, node) => {
					// Format triggers by type for easyer display and navigation
					if (node.triggers) {
						node.triggers_by_type = node.triggers.reduce((dict, trigger) => {
							if (!dict[trigger.triggerType.slug])
								dict[trigger.triggerType.slug] = []
							
							dict[trigger.triggerType.slug].push(trigger)

							return dict
						}, {})
					} else {
						node.triggers_by_type = {}
					}

					dict[node.id] = node
					
					return dict
				}, {})
			},
			async parseScenarioData() {
				// todo: Check if user progression allow him to access this scenario/location

				// Reset UI states
				this.scenePlaying = false
				this.hasSceneEnded = false
				this.scenarioEnd = false
				this.breadcrumbs = [
					{
						parent: 'Home',
						label: 'Chargement...',
					}
				]

				// Reset navigation states
				this.navigationStack = []
				this.navigationSceneStack = []

				this.nodeById = this.getNodeById()

				if (!this.nodeById) {
					this.$router.push({ name: "Home" })
					return
				}

				// Handle game debug preview
				if (this.gamePreview) {
					// Start the scenario
					this.firstNodeId = '0'
					this.startScenario()
					return
				}

				// Reset progress states
				this.visitedNodes = {}
				this.mediaEnded = {}

				// Init game results and scenario nodes stats dictionaries
				this.gameResults = {}
				this.nodesStats = { nodes: this.scenario.nodes.length }

				this.scenario.nodes.forEach((node) => {
					// Count nodes by types
					if (!this.nodesStats[node.type.slug]) {
						this.nodesStats[node.type.slug] = 1
					} else {
						this.nodesStats[node.type.slug] += 1
					}

					// Init game result
					if (node.type.slug == 'game') {
						this.gameResults[node.node_content] = false
					}
				})

				// Get first node by eliminating nodes triggered by others
				const firstNodeCandidates = this.scenario.nodes.reduce((dict, node) => {
					dict[node.id] = true
					
					return dict
				}, {})

				for (var i = 0; i < this.scenario.nodes.length; i++) {
					const node = this.scenario.nodes[i]

					if (node.triggers && node.triggers.length > 0) {
						// Remove nodes triggered by this one
						for (var j = 0; j < node.triggers.length; j++) {
							delete firstNodeCandidates[node.triggers[j].triggered_node_id]
						}
					} else {
						// Remove this node if he doesn't trigger anything
						delete firstNodeCandidates[node.id]
					}
				}

				// Start the scenario
				this.firstNodeId = Object.keys(firstNodeCandidates)[0]

				// Update breadcrumb
				if (this.scenario.locations && this.scenario.locations.length > 0) {
					const location = this.scenario.locations[0].location
					
					const ophSlugs = ['oph_ca', 'oph_dg', 'oph_dgl', 'oph_caleol', 'oph_daf']
					if (ophSlugs.indexOf(location.slug) > -1) {
						this.breadcrumbs[0].parent = 'OPH'
					}

					this.breadcrumbs[0].label = location.title
					this.breadcrumbs[0].to = {
						name: 'Scenario',
						params: {
							id: this.scenario.id,
							nodeId: this.firstNodeId
						},
					}
				}

				await this.startScenario()
			},
			async startScenario() {
				if (!this.firstNodeId || !this.nodeById) {
					this.$router.push({ name: "Home" })
					return
				}

				// Send log data
				const response = await this.dispatchRequest('Logs/save', {
					id: null,
					data_key: this.scenario.id,
					data: {
						nodes: [],
						progress: 0,
						progressPDF: 0,
						progressMedia: 0,
						stats: this.nodesStats,
					},
					type_slug: 'scenario_nodes',
				})

				this.nodesLogId = response.data.insert_logs_one.id

				await this.dispatchRequest('Logs/save', {
					data_key: this.scenario.id,
					data: {
						node_log_id: this.nodesLogId,
					},
					type_slug: 'scenario_start',
				})

				// Set current node
				if (this.nodeId) {
					this.navigateToNode(this.nodeId, true)

					// Reset route to avoid NavigationDuplicated error
					this.$router.push({
						name: 'Scenario',
						params: {
							id: this.id,
						}
					})
				} else {
					this.navigateToNode(this.firstNodeId)
				}
			},
			playPause(event) {
				if (!this.enablePlayPause || this.isLoading)
					return

				// Check for call from keyup event
				if (event && event.keyCode && event.keyCode != 32)
					return

				if (this.$refs.scene) {					
					if (!this.hasSceneEnded) {
						this.$refs.scene.playPause()
					} else {
						// When starting to play a scene after showing her interactive node, go back to the scene node to disable/hide interactive node
						if (this.currentNode && this.currentScene && this.currentNode.type.slug == 'interactive') {
							this.navigateToNode(this.currentScene.id)
						}

						this.hasSceneEnded = false

						this.$refs.scene.restart()
					}
				}
			},
			goToMenu() {
				if (!this.enableMenuButton)
					return

				this.playSoundEffect('menu')

				// Return to first scene
				this.navigateToNode(this.navigationSceneStack[0])

				// Check for interactive node on this scene
				this.checkForInteractiveNode()
			},
			triggerClick(trigger) {
				// Check trigger data
				if (!trigger || !trigger.id || !trigger.triggered_node_id)
					return

				this.playSoundEffect('trigger')

				// Go forward to the triggered node
				this.navigateToNode(trigger.triggered_node_id)
			},
			nextNode(origin) {
				if (!this.currentNode)
					return

				// Count media watched until the end
				if (origin == 'media_end') {
					this.mediaEnded[this.currentNode.id] = true
				}

				// Stop here if we shouldn't go to the next node
				if (!this.currentScene || (!this.hasNextNode && origin == 'next_click') || this.scenarioEnd || this.isLoading)
					return

				// Check if we need to present choices and/or interactive node to the user before going to the next node
				if (this.currentNode.type.slug == 'scene') {
					// Check if the scene has buttons to be shown (and not already showing them)
					const isShowing = (this.hasSceneEnded && !this.scenePlaying)
					const hasButtons = (this.currentScene.triggers_by_type && this.currentScene.triggers_by_type.scene_button &&  this.currentScene.triggers_by_type.scene_button.length > 0)
					if (!isShowing && hasButtons) {
						// Stop the scene
						this.stopScene()

						// Check for interactive node in the default next node
						this.checkForInteractiveNode()

						// Stop here
						return
					}
				}

				let nextNodeId = null

				// Try to get default trigger from current node
				if (this.currentNode.triggers_by_type && this.currentNode.triggers_by_type.continue && this.currentNode.triggers_by_type.continue[0]) {
					// Go to default trigger
					nextNodeId = this.currentNode.triggers_by_type.continue[0].triggered_node_id

					// Prevent navigation when a media end trigger another media node
					if (origin == 'media_end' && this.nodeById[nextNodeId].type.slug == 'media') {
						return
					}
				}

				// If we don't have a default trigger, go back to last (not 'finished') scene
				if (!nextNodeId) {
					// Find the last key node (ie: scene/interactive node)
					let lastKeyNode = null

					for (var i = this.navigationStack.length - 1; i >= 0; i--) {
						const lastNodeId = this.navigationStack[i]
						const node = this.nodeById[lastNodeId]
						let triggers = []

						if (!node)
							continue

						// Check if it's a valid key node and get his triggers
						if (node.type.slug == 'scene') {
							triggers = (node.triggers_by_type && node.triggers_by_type.scene_button ? node.triggers_by_type.scene_button : [])
						} else if (node.type.slug == 'interactive') {
							triggers = (node.triggers_by_type && node.triggers_by_type.interactive_trigger ? node.triggers_by_type.interactive_trigger : [])
						}

						// Check if the node has been 'finished' (ie: all triggered node have been visited)
						if (triggers.length > 0) {
							for (var j = 0; j < triggers.length; j++) {
								const id = triggers[j].triggered_node_id

								// The key node is found if one of the triggered node has not been visited
								if (!this.visitedNodes[id]) {
									lastKeyNode = node
									break
								}
							}

							// Stop if a node is found
							if (lastKeyNode)
								break
						}
					}

					// Go back to the key node, if found
					if (lastKeyNode) {
						nextNodeId = lastKeyNode.id
					}
				}

				if (!nextNodeId) {
					// Compute key nodes progress
					let keyNodesProgress = (this.nodeProgress.scene ? this.nodeProgress.scene : 0)
					keyNodesProgress += (this.nodeProgress.media ? this.nodeProgress.media : 0)

					let keyNodesCount = (this.nodesStats.scene ? this.nodesStats.scene : 0)
					keyNodesCount += (this.nodesStats.media ? this.nodesStats.media : 0)

					// Sanity check
					keyNodesCount = Math.max(1, keyNodesProgress, keyNodesCount)

					// If we still don't have a next node, and we visited all scene and media nodes, end the scenario
					if (keyNodesProgress >= keyNodesCount) {
						this.endScenario()
					} else if (this.navigationSceneStack.length > 0) {
						// Go back to the start/menu
						this.navigateToNode(this.navigationSceneStack[0])
					}
				} else {
					// Go to next node
					this.navigateToNode(nextNodeId)
				}
			},
			previousNode() {
				if (!this.currentNode || !this.hasPreviousNode || this.isLoading)
					return

				let previousNodeId = this.navigationStack[this.navigationStack.length - 2]

				// Skip interactive node if needed
				if (this.currentNode.type.slug == 'interactive' && this.navigationStack.length > 2) {
					previousNodeId = this.navigationStack[this.navigationStack.length - 3]
				}

				this.navigateToNode(previousNodeId)
			},
			navigateToNode(id, resetNavigationStack = false) {
				// Handle navigation logic
				if (this.scenarioEnd || !this.nodeById || !id)
					return

				// Don't navigate to current node
				if (this.currentNode && this.currentNode.id == id)
					return

				// Check resulting node
				const node = this.nodeById[id]

				if (!this.nodeById[id])
					return

				// Try to find the node we want to navigate to in the history
				const goingBack = (this.navigationStack.find((nodeId) => (nodeId == id)) ? true : false)

				// Update the navigation stack
				if (goingBack) {
					while (this.navigationStack.length > 0) {
						// Update navigation stack
						const nodeId = this.navigationStack.pop()

						// Update scene stack if needed
						const lastSceneId = (this.navigationSceneStack.length > 0 ? this.navigationSceneStack[this.navigationSceneStack.length - 1] : null)

						if (lastSceneId && nodeId == lastSceneId) {
							this.navigationSceneStack.pop()
						}

						// If we found the node, stop updating
						if (nodeId == id) {
							break
						}
					}
				} else if (resetNavigationStack) {
					// Stop current scene
					this.stopScene()

					// Reset navigation states
					this.navigationStack = []
					this.navigationSceneStack = []

					let nextNode = this.nodeById[this.firstNodeId]
					let triggers = []

					while (nextNode && nextNode.id != id) {
						// Update navigation stack
						this.navigationStack.push(nextNode.id)

						// Update scene stack if needed
						if (nextNode.type.slug == 'scene') {
							this.navigationSceneStack.push(nextNode.id)
						}

						if (nextNode.triggers.length > 0) {
							for (var i = nextNode.triggers.length - 1; i >= 1; i--) {
								triggers.push(nextNode.triggers[i])
							}

							nextNode = this.nodeById[nextNode.triggers[0].triggered_node_id]
						} else if (triggers.length > 0) {
							// Save current node to update navigation stack
							let previousNode = nextNode
							
							// Go to next trigger/branch
							nextNode = this.nodeById[triggers.pop().triggered_node_id]

							// Loop until we found the nextNode parent to update navigation stack
							while (this.navigationStack.length > 0 && !previousNode.triggers.find((trigger) => (trigger.triggered_node_id == nextNode.id))) {
								// Update navigation stack
								this.navigationStack.pop()

								// Update scene stack if needed
								const lastSceneId = (this.navigationSceneStack.length > 0 ? this.navigationSceneStack[this.navigationSceneStack.length - 1] : null)

								if (lastSceneId && previousNode.id == lastSceneId) {
									this.navigationSceneStack.pop()
								}

								// Update previous node
								if (this.navigationStack.length > 0) {
									previousNode = this.nodeById[this.navigationStack[this.navigationStack.length - 1]]
								}
							}
						}
					}
				}

				// Stop current scene
				this.stopScene()

				// Update navigation stack and scene stack if needed
				if (node.type.slug == 'scene') {
					// Update scene state
					this.hasSceneEnded = goingBack

					this.navigationSceneStack.push(id)
				} else if (node.type.slug == 'interactive') {
					// Update scene state
					this.hasSceneEnded = true
				}

				this.navigationStack.push(id)

				// Update current node
				this.currentNode = node

				// Mark node as visited
				this.$set(this.visitedNodes, node.id, true)

				// Compute key nodes progress
				let keyNodesProgress = (this.nodeProgress.scene ? this.nodeProgress.scene : 0)
				keyNodesProgress += (this.nodeProgress.media ? this.nodeProgress.media : 0)

				let keyNodesCount = (this.nodesStats.scene ? this.nodesStats.scene : 0)
				keyNodesCount += (this.nodesStats.media ? this.nodesStats.media : 0)

				// Sanity check
				keyNodesCount = Math.max(1, keyNodesProgress, keyNodesCount)

				// Always send the visited nodes log to have an accurate updated_at date (used for 'session' time stats)
				// Todo: faire un gros envoie plutot que plein ? (ou regrouper cette stats ?) / throttle ?
				this.$store.dispatch('Logs/save', {
					id: this.nodesLogId,
					data_key: this.scenario.id,
					data: {
						nodes: Object.keys(this.visitedNodes),
						progress: (keyNodesProgress / keyNodesCount),
						progressPDF: ((this.nodeProgress.pdf ? this.nodeProgress.pdf : 0) / (this.nodesStats.pdf ? this.nodesStats.pdf : 1)),
						progressMedia: (Object.keys(this.mediaEnded).length / (this.nodesStats.media ? this.nodesStats.media : 1)),
						stats: this.nodesStats,
					},
					type_slug: 'scenario_nodes',
				})

				// Send media history log if needed
				if (node.node_content) {
					let type_slug = null

					switch (node.type.slug) {
						case 'pdf':
							type_slug = 'view_pdf'
							break

						case 'media':
							type_slug = 'media_start'
							break
					}

					if (type_slug) {
						this.$store.dispatch('Logs/addMediaHistory', {
							data_key: node.node_content,
							data: {
								scenario_id: this.scenario.id,
								node_id: node.id,
								node_title: node.title,
								node_log_id: this.nodesLogId,
							},
							type_slug,
						})
					}
				}


				// Check for interactive node on scene, if it's a static one
				if (node.type.slug == 'scene' && this.isAssetStatic(node.node_content)) {
					this.checkForInteractiveNode()
				}

				// Play sound effect
				if (node.type.slug == 'pdf') {
					this.playSoundEffect('pdf')
				}

				// Update breadcrumb
				let breadcrumb = null
				
				if (this.nodeBreadcrumbs.length > 0) {
					// Find current chapter in node stack
					breadcrumb = this.nodeBreadcrumbs.find((breadcrumb) => {
						return (this.navigationStack.indexOf(breadcrumb.nodeId) > -1)
					})
				} else {
					// Find last title in scenes
					for (var j = this.navigationSceneStack.length - 1; j >= 0; j--) {
						const scene = this.nodeById[this.navigationSceneStack[j]]

						if (scene.title && scene.title.toLowerCase() != this.breadcrumbs[0].label.toLowerCase()) {
							breadcrumb = {
								label: scene.title,
							}
							break
						}
					}
				}

				if (this.breadcrumbs.length > 1) {
					if (breadcrumb) {
						this.breadcrumbs[1].label = breadcrumb.label
					} else {
						this.$delete(this.breadcrumbs, 1)
					}
				} else if (breadcrumb) {
					this.breadcrumbs.push({
						label: breadcrumb.label
					})
				}
			},
			checkForInteractiveNode() {
				// Only check for scene
				if (!this.currentNode || this.currentNode.type.slug != 'scene')
					return

				// Try to get default trigger from current node
				if (this.currentNode.triggers_by_type && this.currentNode.triggers_by_type.continue && this.currentNode.triggers_by_type.continue[0]) {

					const id = this.currentNode.triggers_by_type.continue[0].triggered_node_id

					// Automaticly display interactive node if present
					if (this.nodeById[id].type.slug == 'interactive') {
						this.nextNode('interactive_check')
					}
				}
			},
			endScenario() {
				// Stop current scene
				this.stopScene()

				// Send log data
				this.$store.dispatch('Logs/save', {
					data_key: this.scenario.id,
					data: {
						node_log_id: this.nodesLogId,
					},
					type_slug: 'scenario_end',
				})

				// Navigate to the main scenario of this location when displaying a 'introduction' scenario
				if (this.scenario.type.slug == 'intro') {
					// Get current location
					if (this.scenario.locations && this.scenario.locations.length > 0) {
						const location = this.scenario.locations[0].location

						// Show OPH scenario selection
						if (location.slug == 'oph') {
							this.$router.push({ name: 'OPH' })
							return
						}

						// Find main scenario of this locations
						const scenario = this.$store.state.Scenarios.list.find((scenario) => {
							if (scenario.type.slug == 'scenario') {
								for (var i = 0; i < scenario.locations.length; i++) {
									const id = scenario.locations[i].id

									if (location.id == id) {
										return true
									}
								}

							}

							return false
						})

						if (scenario) {
							this.$router.push({
								name: 'Scenario',
								params: {
									id: scenario.id
								}
							})

							return
						}
					}

					// If no location, or no scenario, go back to map
					this.$router.push({ name: "Home" })
					return
				}

				this.playSoundEffect('end')

				this.scenarioEnd = true
			},
			sceneEnded() {
				if (this.hasSceneEnded)
					return

				// Update state
				this.hasSceneEnded = true

				// Sanity check
				if (this.currentNode && this.currentNode.id == this.currentScene.id) {
					// Trigger next node logic
					this.nextNode('scene_end')
				}
			},
			gameEnded(results) {
				if (!results)
					return

				// Save best game results data
				const gameId = this.currentNode.node_content
				const value = (results.right / results.total)

				if (!this.gameResults[gameId] || this.gameResults[gameId].value < value) {
					this.gameResults[gameId] = {
						value,
						results,
					}
				}

				// Send log data
				this.$store.dispatch('Logs/save', {
					data_key: gameId,
					data: {
						value,
						results,
						scenario_id: this.scenario.id,
						node_log_id: this.nodesLogId,
					},
					type_slug: 'game_results',
				})
			},
			setScenePlaying(scenePlaying) {
				this.scenePlaying = scenePlaying
			},
			stopScene() {
				if (this.$refs.scene)
					this.$refs.scene.stop()

				this.scenePlaying = false
				this.hasSceneEnded = true
			},
			getButtonIcon(button) {
				if (!this.nodeById || !button.triggered_node_id)
					return ''

				// Get triggered node type
				const slug = (this.nodeById[button.triggered_node_id] && this.nodeById[button.triggered_node_id].type ? this.nodeById[button.triggered_node_id].type.slug : '')

				switch (slug) {
					case 'scene':
						return ''
					case 'game':
						return require('../assets/images/game-icon.svg')
					case 'pdf':
						return require('../assets/images/pdf-icon.svg')
					case 'media':
						return require('../assets/images/media-icon.svg')
					case 'simple_proposal':
						return require('../assets/images/text-icon.svg')
					case 'interactive':
						return require('../assets/images/interactive-icon.svg')
				}

				return ''
			},
			isAssetStatic(assetId) {
				// Check asset path of current scene
				const assetPath = (this.assets[assetId] && this.assets[assetId].path && this.assets[assetId].path)

				return (assetPath && 
					assetPath.indexOf('mp4') < 0 && 
					assetPath.indexOf('m4v') < 0)
			},
		},
	}
</script>

<style lang="scss" scoped>

.scenario {
	&-nav {
		bottom: 3.4rem;
	}

	&-menu-button {
		@apply mt-36;
		top: 0;
		left: 20px;

		p {
			opacity: 0;
			transition: opacity .5s;
		}

		&-container {
			@apply h-16 w-16;

			img {
				max-width: 100%;
				max-height: 100%;
			}

			&:hover {
				& + p {
					opacity: 1;
				}
			}
		}
	}

	&-buttons {
		width: 100%;
	}

	&-buttons button {
		@apply relative rounded-btn text-left text-xs mx-2 mb-4;
		padding: 5px 12px;
		width: 18%;
		min-width: auto;

		&.wide {
			width: 22%;
		}

		/* Content: Icon & Text */
		.content {
			@apply flex items-center;
			margin-bottom: 10px;
			flex-grow: 1;
			width: 100%;

			img {
				margin-right: 10px;
				width: 20px;
				height: 20px;

				// Hide button without icon
				&[src=''] {
					height: 0px !important;
				}
			}

			span {
				max-height: 6rem;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}

		@screen xxl {
			@apply text-lg;

			.content {
				img {
					width: 40px;
					height: 40px;
					margin-right: 14px;
				}
			}
		}

		@screen xxxl {
			max-width: 20%;

			&.wide {
				max-width: 22%;
			}
		}

		/* Progress bar */
		.progress-container {
			@apply bg-white-transparent absolute;
			left: 12px;
			right: 12px;
			bottom: 12px;
			height: 1px;
			flex-shrink: 0;
		}

		.progress {
			@apply bg-white;
			height: 100%;
			width: 0;
		}

		/* Triggered style */
		&.triggered {
			@apply bg-blue-gray;

			.progress {
				width: 100%;
			}
		}
	}

	&-controls {
		div {
			height: 100%;
		}

		img {
			width: auto;
			height: 100%;
		}

		.disabled {
			@apply cursor-not-allowed;
			opacity: 0.4;
			transition: opacity 1s;
		}
	}
}

</style>
